import React, {
    useState, useEffect, useRef
} from 'react';
import { createPortal } from 'react-dom';

import {
    ShieldAlert, Calendar, CheckCircle, AlertTriangle, Activity, X, Trash2
} from 'lucide-react';

import axios from 'axios';

import SecurityChatbot from '../components/SecurityChatbot';
import { formatImageUrl } from '../utils/urlHelper';

const SecurityDashboard = () => {
    const userRole = localStorage.getItem('userRole') || 'VIEWER';
    const isDemo = userRole === 'DEMO_USER';
    const canWrite = userRole === 'COMPANY_ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'PROJECT_MANAGER';

    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIncident, setSelectedIncident] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const lastScannedId = useRef(null);

    useEffect(() => {
        if (selectedIncident) {
            if (selectedIncident.id !== lastScannedId.current) {
                setIsScanning(true);
                lastScannedId.current = selectedIncident.id;
                const timer = setTimeout(() => {
                    setIsScanning(false);
                }, 1500);
                return () => clearTimeout(timer);
            }
        } else {
            setIsScanning(false);
            lastScannedId.current = null;
        }
    }, [selectedIncident]);

    useEffect(() => {
        const mainEl = document.querySelector('main');
        if (selectedIncident) {
            document.body.style.overflow = 'hidden';
            if (mainEl) mainEl.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            if (mainEl) mainEl.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
            if (mainEl) mainEl.style.overflow = '';
        };
    }, [selectedIncident]);

    const renderBoundingBoxes = (incident) => {
        if (!incident) return null;

        let allBoxes = [];
        if (incident.boundingBoxes && Array.isArray(incident.boundingBoxes) && incident.boundingBoxes.length > 0) {
            allBoxes = incident.boundingBoxes;
        } else {
            // Generate mock fallback boxes
            const seed = incident.id || 1;
            const offset = (seed * 7) % 15;
            if (incident.isResolved) {
                if (incident.type === 'Falta de Casco') {
                    allBoxes.push({ top: 15, left: 35 + offset, width: 25, height: 70, label: 'Persona (EPP Verificado)' });
                    allBoxes.push({ top: 18, left: 42 + offset, width: 11, height: 12, label: 'Casco Verificado' });
                } else if (incident.type === 'Falta de Chaleco') {
                    allBoxes.push({ top: 12, left: 30 + offset, width: 28, height: 75, label: 'Persona (EPP Verificado)' });
                    allBoxes.push({ top: 28, left: 36 + offset, width: 16, height: 25, label: 'Chaleco Verificado' });
                } else if (incident.type === 'Trabajo en Altura sin Arnes') {
                    allBoxes.push({ top: 10, left: 40 + (seed % 10), width: 22, height: 65, label: 'Persona (Trabajo Seguro)' });
                    allBoxes.push({ top: 35, left: 44 + (seed % 10), width: 14, height: 20, label: 'Arnés Verificado' });
                }
            } else {
                if (incident.type === 'Falta de Casco') {
                    allBoxes.push({ top: 15, left: 35 + offset, width: 25, height: 70, label: 'Persona (Sin EPP)' });
                    allBoxes.push({ top: 18, left: 42 + offset, width: 11, height: 12, label: 'Sin Casco Detectado' });
                } else if (incident.type === 'Falta de Chaleco') {
                    allBoxes.push({ top: 12, left: 30 + offset, width: 28, height: 75, label: 'Persona (Sin EPP)' });
                    allBoxes.push({ top: 28, left: 36 + offset, width: 16, height: 25, label: 'Sin Chaleco Detectado' });
                } else if (incident.type === 'Trabajo en Altura sin Arnes') {
                    allBoxes.push({ top: 10, left: 40 + (seed % 10), width: 22, height: 65, label: 'Persona (Sin EPP)' });
                    allBoxes.push({ top: 35, left: 44 + (seed % 10), width: 14, height: 20, label: 'Sin Arnés de Seguridad' });
                }
            }
        }

        // Filter and render person boxes
        const personBoxes = allBoxes.filter(box => box.label.toLowerCase().includes('persona'));
        
        if (personBoxes.length === 0) {
            // Safety fallback
            return allBoxes.map((box, idx) => (
                <div
                    key={idx}
                    className="absolute border border-solid border-red-500/40 rounded-xl"
                    style={{
                        top: `${box.top}%`,
                        left: `${box.left}%`,
                        width: `${box.width}%`,
                        height: `${box.height}%`
                    }}
                />
            ));
        }

        const isResolved = incident.isResolved;

        return personBoxes.map((P, idx) => {
            // Find child sub-boxes overlapping with this person
            const subBoxes = allBoxes.filter(box => !box.label.toLowerCase().includes('persona'));
            const infractions = [];
            
            subBoxes.forEach(sub => {
                const subMidX = sub.left + sub.width / 2;
                const subMidY = sub.top + sub.height / 2;
                const isInside = 
                    subMidX >= P.left &&
                    subMidX <= P.left + P.width &&
                    subMidY >= P.top &&
                    subMidY <= P.top + P.height;
                    
                if (isInside) {
                    const lbl = sub.label.toLowerCase();
                    if (lbl.includes('casco') && !lbl.includes('verificado')) infractions.push('helmet');
                    if (lbl.includes('chaleco') && !lbl.includes('verificado')) infractions.push('vest');
                    if (lbl.includes('arnés') || lbl.includes('arnes')) {
                        if (!lbl.includes('verificado')) infractions.push('harness');
                    }
                }
            });

            // Fallback for unresolved state
            if (!isResolved && infractions.length === 0) {
                if (incident.type === 'Falta de Casco') infractions.push('helmet');
                else if (incident.type === 'Falta de Chaleco') infractions.push('vest');
                else if (incident.type === 'Trabajo en Altura sin Arnes') infractions.push('harness');
            }

            let borderColor = '#ef4444'; // default Red
            
            if (isResolved) {
                borderColor = '#10b981'; // Green
            } else {
                if (infractions.includes('helmet')) {
                    borderColor = '#ef4444'; // Red
                } else if (infractions.includes('harness')) {
                    borderColor = '#f97316'; // Orange
                } else if (infractions.includes('vest')) {
                    borderColor = '#eab308'; // Yellow
                } else {
                    // Fallback based on incident type
                    if (incident.type === 'Falta de Casco') {
                        borderColor = '#ef4444';
                    } else if (incident.type === 'Falta de Chaleco') {
                        borderColor = '#eab308';
                    } else if (incident.type === 'Trabajo en Altura sin Arnes') {
                        borderColor = '#f97316';
                    }
                }
            }

            return (
                <div
                    key={idx}
                    className="absolute pointer-events-auto transition-all duration-300 group cursor-help"
                    style={{
                        top: `${P.top}%`,
                        left: `${P.left}%`,
                        width: `${P.width}%`,
                        height: `${P.height}%`
                    }}
                >
                    {/* Top-Left Corner */}
                    <div 
                        className="absolute top-0 left-0 w-4 h-4 border-t-[3px] border-l-[3px] transition-all duration-300 group-hover:scale-110" 
                        style={{ 
                            borderColor: borderColor,
                            filter: `drop-shadow(0 0 3.5px ${borderColor})`
                        }}
                    />
                    {/* Top-Right Corner */}
                    <div 
                        className="absolute top-0 right-0 w-4 h-4 border-t-[3px] border-r-[3px] transition-all duration-300 group-hover:scale-110" 
                        style={{ 
                            borderColor: borderColor,
                            filter: `drop-shadow(0 0 3.5px ${borderColor})`
                        }}
                    />
                    {/* Bottom-Left Corner */}
                    <div 
                        className="absolute bottom-0 left-0 w-4 h-4 border-b-[3px] border-l-[3px] transition-all duration-300 group-hover:scale-110" 
                        style={{ 
                            borderColor: borderColor,
                            filter: `drop-shadow(0 0 3.5px ${borderColor})`
                        }}
                    />
                    {/* Bottom-Right Corner */}
                    <div 
                        className="absolute bottom-0 right-0 w-4 h-4 border-b-[3px] border-r-[3px] transition-all duration-300 group-hover:scale-110" 
                        style={{ 
                            borderColor: borderColor,
                            filter: `drop-shadow(0 0 3.5px ${borderColor})`
                        }}
                    />
                </div>
            );
        });
    };

    // 1. Cargar incidentes desde API
    const fetchIncidents = async () => {
        try {
            const response = await axios.get('http://localhost:4000/api/incidents');
            if (response.data && Array.isArray(response.data)) {
                const formattedIncidents = response.data.map(incident => ({
                    id: incident.id,
                    url: formatImageUrl(incident.evidence_image_path),
                    type: incident.incident_type,
                    confidence: Math.round(incident.confidence_score * 100),
                    isResolved: incident.is_resolved,
                    date: new Date(incident.created_at).toLocaleString(),
                    camera: incident.camera_id,
                    boundingBoxes: incident.bounding_boxes || null
                }));
                setIncidents(formattedIncidents);
            }
        } catch (error) {
            console.error("Error conectando a la API de incidentes:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIncidents();
    }, []);

    const handleResolve = async (id, currentResolved) => {
        try {
            await axios.put(`http://localhost:4000/api/incidents/${id}`, { isResolved: !currentResolved });
            setIncidents(prev => prev.map(inc => inc.id === id ? { ...inc, isResolved: !currentResolved } : inc));
            if (selectedIncident && selectedIncident.id === id) {
                setSelectedIncident(prev => ({ ...prev, isResolved: !currentResolved }));
            }
        } catch (error) {
            console.error("Error al resolver incidente:", error);
            alert("No se pudo actualizar el estado del incidente.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas descartar/eliminar permanentemente este incidente?")) {
            return;
        }
        try {
            await axios.delete(`http://localhost:4000/api/incidents/${id}`);
            setIncidents(prev => prev.filter(inc => inc.id !== id));
            setSelectedIncident(null);
        } catch (error) {
            console.error("Error al eliminar incidente:", error);
            alert("No se pudo eliminar el incidente.");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(220,38,38,0.1),rgba(255,255,255,0))] font-sans p-4 md:p-8 animate-fade-in relative z-10 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mb-4"></div>
                    <p className="text-xl font-light text-theme-text-primary">Conectando con sistemas de seguridad...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-theme-bg font-sans p-4 md:p-8 animate-fade-in relative z-10 pb-20">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                    <div>
                        <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3 mb-2">
                            <ShieldAlert className="text-red-500 drop-shadow-md" size={36} /> Centro de Seguridad <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-rose-600">EHS</span>
                        </h1>
                        <p className="text-theme-text-secondary font-medium"> Monitoreo integral de infracciones y riesgos en tiempo real. </p>
                    </div>
                    {/* Estadísticas Rápidas */}
                    <div className="flex gap-4">
                        <div className="backdrop-blur-xl px-7 py-4 rounded-2xl border border-theme-border flex flex-col items-center shadow-2xl relative overflow-hidden group hover:border-red-500/30 transition-colors">
                            <div className="absolute inset-0 bg-gradient-to-t from-red-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <span className="text-4xl font-black text-red-500 relative z-10">{incidents.filter(i => !i.isResolved).length}</span>
                            <span className="text-xs text-theme-text-secondary uppercase tracking-widest mt-1 font-bold relative z-10">Alertas</span>
                        </div>
                        <div className="backdrop-blur-xl px-7 py-4 rounded-2xl border border-theme-border flex flex-col items-center shadow-2xl relative overflow-hidden group hover:border-emerald-500/30 transition-colors">
                            <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <span className="text-4xl font-black text-emerald-400 relative z-10">{incidents.filter(i => i.isResolved).length}</span>
                            <span className="text-xs text-theme-text-secondary uppercase tracking-widest mt-1 font-bold relative z-10">Resueltos</span>
                        </div>
                    </div>
                </div>

                {incidents.length === 0 ? (
                    <div className="max-w-7xl mx-auto backdrop-blur-xl p-16 rounded-3xl border border-theme-border text-center shadow-2xl">
                        <div className="bg-emerald-500/10 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/20 shadow-inner">
                            <CheckCircle size={48} className="text-emerald-500" />
                        </div>
                        <h2 className="text-3xl font-bold mb-3 tracking-tight text-theme-text-primary">Zona Segura y Controlada</h2>
                        <p className="text-theme-text-secondary max-w-md mx-auto text-lg">El sitio de construcción cumple actualmente con todas las normativas EHS. Sin incidentes activos.</p>
                    </div>
                ) : (
                    /* Grid de Incidentes */
                    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {incidents.map((incident) => (
                            <div key={incident.id} className="bg-theme-panel/80 backdrop-blur-xl rounded-3xl overflow-hidden border border-theme-border shadow-2xl group hover:border-red-500/50 hover:shadow-red-900/20 hover:-translate-y-1 transition-all duration-300">
                                {/* Imagen Evidence */}
                                <div onClick={() => setSelectedIncident(incident)} className="relative h-64 overflow-hidden border-b border-theme-border cursor-pointer group">
                                    <img src={incident.url} alt="Evidencia" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-300 text-white font-bold text-sm tracking-wider uppercase backdrop-blur-sm z-20">
                                        Verificar Evidencia
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#111114] via-transparent to-transparent opacity-80 pointer-events-none"></div>
                                    <div className={`absolute top-4 right-4 backdrop-blur-xl px-4 py-2 rounded-xl border flex items-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-lg z-10 ${incident.isResolved ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300' : 'bg-red-500/20 border-red-500/30 text-red-300 shadow-red-500/20'}`}>
                                        {incident.isResolved ? <CheckCircle size={14} /> : <AlertTriangle size={14} className="animate-pulse" />}
                                        {incident.isResolved ? 'Solucionado' : 'Alerta Viva'}
                                    </div>
                                    <div className="absolute bottom-4 left-4 bg-theme-bg/60 backdrop-blur-md px-3 py-1.5 rounded-lg border border-theme-border flex items-center gap-2 text-xs font-bold shadow-xl z-10">
                                        <Activity size={14} className="text-blue-400" /> IA PRED: {incident.confidence}%
                                    </div>
                                </div>
                                {/* Contenido / Info */}
                                <div className="p-6 relative">
                                    <h3 onClick={() => setSelectedIncident(incident)} className="text-xl font-bold text-theme-text-primary mb-2 tracking-tight hover:text-red-400 cursor-pointer transition-colors">{incident.type}</h3>
                                    <div className="flex flex-col gap-3 mt-4 text-sm text-theme-text-secondary">
                                        <div className="flex items-center gap-2 w-fit px-3 py-1.5 rounded-lg border border-theme-border">
                                            <Calendar size={14} className="text-theme-text-secondary" />
                                            <span className="font-mono text-xs">{incident.date}</span>
                                        </div>
                                        <div className="flex items-center gap-2 w-fit px-3 py-1.5 rounded-lg border border-theme-border">
                                            <ShieldAlert size={14} className="text-theme-text-secondary" />
                                            <span className="font-mono text-xs">Cámara SRC-{incident.camera}</span>
                                        </div>
                                    </div>
                                    {/* Footer del card */}
                                    <div className="mt-8 pt-5 border-t border-theme-border flex justify-between items-center">
                                        <span className="text-xs font-mono text-theme-text-secondary">ID: {String(incident.id)}</span>
                                        <button onClick={() => setSelectedIncident(incident)} className="text-xs bg-red-500/10 hover:bg-red-500/30 text-red-400 px-4 py-2 rounded-xl font-black uppercase tracking-wider transition-all border border-red-500/20 shadow-lg hover:shadow-red-500/20">
                                            Verificar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <SecurityChatbot />
            </div>

            {selectedIncident && createPortal(
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem',
                        zIndex: 9999,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)'
                    }}
                    className="animate-fade-in animate-duration-200"
                >
                    <div className="bg-theme-panel border border-theme-border rounded-3xl overflow-hidden shadow-2xl max-w-7xl w-full flex flex-col md:flex-row relative z-10 h-auto max-h-[90vh] overflow-y-auto md:h-[80vh] md:max-h-[80vh] md:overflow-hidden">
                        <button onClick={() => setSelectedIncident(null)} className="absolute top-4 right-4 z-30 bg-theme-bg/60 hover:bg-theme-bg/90 border border-theme-border text-theme-text-primary p-2 rounded-xl transition-all" title="Cerrar ventana">
                            <X size={18} />
                        </button>
                        
                        {/* Panel Izquierdo: Imagen Ampliada (70% de ancho) */}
                        <div className="w-full md:w-[70%] bg-black flex items-center justify-center p-6 relative overflow-hidden h-[350px] md:h-full">
                            <div className="relative max-w-full max-h-full flex items-center justify-center">
                                <div className="relative inline-block max-w-full max-h-[74vh] overflow-hidden rounded-2xl border border-theme-border/20 shadow-xl bg-slate-950">
                                    <img 
                                        src={selectedIncident.url} 
                                        alt="Evidencia Ampliada" 
                                        className="max-h-[74vh] max-w-full object-contain block transition-transform duration-300 hover:scale-[1.02]" 
                                    />
                                    {/* Escáner de IA */}
                                    {isScanning && (
                                        <div className="absolute inset-x-0 w-full h-1 bg-cyan-400 shadow-[0_0_15px_#22d3ee] pointer-events-none animate-scan z-20" />
                                    )}
                                    {isScanning && (
                                        <div className="absolute inset-0 bg-cyan-950/20 backdrop-brightness-110 pointer-events-none z-10 flex items-center justify-center">
                                            <span className="bg-black/80 text-cyan-400 border border-cyan-500/30 text-xs font-mono font-bold px-3 py-1.5 rounded-xl uppercase tracking-widest animate-pulse">
                                                Analizando evidencia...
                                            </span>
                                        </div>
                                    )}
                                    {/* Cajas de detección */}
                                    {!isScanning && renderBoundingBoxes(selectedIncident)}
                                </div>
                            </div>
                                               {/* Floating Legend/Simbología EHS overlay at the bottom-left of the image container */}
                            {!isScanning && (
                                <div className="absolute bottom-4 left-4 bg-slate-950/95 border border-theme-border/30 rounded-xl px-4 py-3 flex items-center gap-5 text-[11px] md:text-[12px] font-semibold tracking-wider uppercase text-theme-text-secondary shadow-lg z-30 backdrop-blur-md">
                                    <span className="text-theme-text-primary font-black">Simbología:</span>
                                    <div className="flex items-center gap-2.5 border-r border-theme-border/20 pr-5">
                                        <div className="w-4 h-4 relative flex-shrink-0">
                                            <div className="absolute top-0 left-0 w-1.5 h-1.5 border-t-2 border-l-2 border-red-500" />
                                            <div className="absolute top-0 right-0 w-1.5 h-1.5 border-t-2 border-r-2 border-red-500" />
                                            <div className="absolute bottom-0 left-0 w-1.5 h-1.5 border-b-2 border-l-2 border-red-500" />
                                            <div className="absolute bottom-0 right-0 w-1.5 h-1.5 border-b-2 border-r-2 border-red-500" />
                                        </div>
                                        <span>Falta de Casco</span>
                                    </div>
                                    <div className="flex items-center gap-2.5 border-r border-theme-border/20 pr-5">
                                        <div className="w-4 h-4 relative flex-shrink-0">
                                            <div className="absolute top-0 left-0 w-1.5 h-1.5 border-t-2 border-l-2 border-yellow-500" />
                                            <div className="absolute top-0 right-0 w-1.5 h-1.5 border-t-2 border-r-2 border-yellow-500" />
                                            <div className="absolute bottom-0 left-0 w-1.5 h-1.5 border-b-2 border-l-2 border-yellow-500" />
                                            <div className="absolute bottom-0 right-0 w-1.5 h-1.5 border-b-2 border-r-2 border-yellow-500" />
                                        </div>
                                        <span>Falta de Chaleco</span>
                                    </div>
                                    <div className="flex items-center gap-2.5 border-r border-theme-border/20 pr-5">
                                        <div className="w-4 h-4 relative flex-shrink-0">
                                            <div className="absolute top-0 left-0 w-1.5 h-1.5 border-t-2 border-l-2 border-orange-500" />
                                            <div className="absolute top-0 right-0 w-1.5 h-1.5 border-t-2 border-r-2 border-orange-500" />
                                            <div className="absolute bottom-0 left-0 w-1.5 h-1.5 border-b-2 border-l-2 border-orange-500" />
                                            <div className="absolute bottom-0 right-0 w-1.5 h-1.5 border-b-2 border-r-2 border-orange-500" />
                                        </div>
                                        <span>Falta de Arnés</span>
                                    </div>
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-4 h-4 relative flex-shrink-0">
                                            <div className="absolute top-0 left-0 w-1.5 h-1.5 border-t-2 border-l-2 border-emerald-500" />
                                            <div className="absolute top-0 right-0 w-1.5 h-1.5 border-t-2 border-r-2 border-emerald-500" />
                                            <div className="absolute bottom-0 left-0 w-1.5 h-1.5 border-b-2 border-l-2 border-emerald-500" />
                                            <div className="absolute bottom-0 right-0 w-1.5 h-1.5 border-b-2 border-r-2 border-emerald-500" />
                                        </div>
                                        <span className="text-emerald-400">Verificado</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Panel Derecho: Telemetría e Intervención (30% de ancho, scroll de fallback) */}
                        <div className="w-full md:w-[30%] p-5 md:p-6 flex flex-col justify-between border-t md:border-t-0 md:border-l border-theme-border bg-theme-panel/40 h-auto md:h-full overflow-y-auto custom-scrollbar md:overflow-hidden">
                            <div>
                                <span className={`text-[10px] px-3 py-1.5 rounded-lg border font-black uppercase tracking-widest ${selectedIncident.isResolved ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                                    {selectedIncident.isResolved ? 'Solucionado' : 'Alerta Viva'}
                                </span>
                                <h3 className="text-2xl font-black text-theme-text-primary mt-2.5 leading-tight">{selectedIncident.type}</h3>
                                
                                <div className="flex flex-col gap-2 mt-2.5">
                                    <div className="flex items-center gap-2 text-xs text-theme-text-secondary font-mono border border-theme-border px-3 py-1.5 rounded-lg w-fit">
                                        <Calendar size={14} />
                                        <span>Fecha: {selectedIncident.date}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-theme-text-secondary font-mono border border-theme-border px-3 py-1.5 rounded-lg w-fit">
                                        <ShieldAlert size={14} />
                                        <span>Origen: Cámara SRC-{selectedIncident.camera}</span>
                                    </div>
                                </div>
 
                                <div className="mt-4 bg-theme-bg/50 border border-theme-border rounded-2xl p-3.5 flex flex-col gap-3">
                                    <div className="flex justify-between items-center text-sm font-bold text-theme-text-primary">
                                        <span>Confianza de IA</span>
                                        <span className="text-blue-400 font-mono text-base">{selectedIncident.confidence}%</span>
                                    </div>
                                    <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden border border-theme-border/20">
                                        <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full" style={{ width: `${selectedIncident.confidence}%` }}></div>
                                    </div>
                                    <div className="text-[10px] text-theme-text-secondary leading-relaxed mt-1">
                                        * Basado en la telemetría del modelo COCO-SSD y el análisis semántico de nombres de archivo de cámaras IoT.
                                    </div>
                                </div>
                                            {canWrite ? (
                                <div className="mt-4 pt-3.5 border-t border-theme-border flex flex-col gap-3">
                                    <button onClick={() => handleResolve(selectedIncident.id, selectedIncident.isResolved)} className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg ${selectedIncident.isResolved ? 'bg-amber-600/10 hover:bg-amber-600/20 text-amber-400 border border-amber-500/20' : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-950/20'}`}>
                                        {selectedIncident.isResolved ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
                                        {selectedIncident.isResolved ? "Reabrir Alerta" : "Resolver Infracción"}
                                    </button>
                                    <button onClick={() => handleDelete(selectedIncident.id)} className="w-full py-3 rounded-xl font-bold bg-red-950/20 hover:bg-red-950/40 text-red-400 border border-red-500/20 flex items-center justify-center gap-2 transition-all active:scale-95">
                                        <Trash2 size={18} />
                                        <span>Descartar (Falso Positivo)</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-4 pt-3.5 border-t border-theme-border text-center text-xs text-theme-text-secondary">
                                    {isDemo ? (
                                        <span className="text-yellow-500 font-semibold">Modo Demo: Resolución deshabilitada</span>
                                    ) : (
                                        <span className="text-blue-400 font-semibold">Modo Lector: No tienes permisos para resolver alertas</span>
                                    )}
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};
export default SecurityDashboard;
